import React, { useCallback, useEffect, useState } from "react";
import { FaFileInvoiceDollar } from "react-icons/fa";
import { SectionCard, MiniStat } from "../SectionCard";
import { useInView } from "../useInView";
import { fetchOrgBillingDetail } from "../api";
import { fmtN, fmtMoney } from "../format";

const ACCENT = "#059669";

const currentMonthAndYear = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

function DailyBars({ loans }) {
  const byDate = {};
  (loans || []).forEach((loan) => {
    if (!loan.fundedDate || !loan.isBillable) return;
    byDate[loan.fundedDate] = (byDate[loan.fundedDate] || 0) + 1;
  });
  const series = Object.entries(byDate)
    .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
    .map(([, count]) => count);
  if (!series.length) return null;
  const max = Math.max(...series, 1);
  return (
    <div className="oo-spark" aria-hidden="true">
      {series.map((v, i) => (
        <div
          key={i}
          className="oo-spark-bar"
          style={{ height: `${Math.max(4, (v / max) * 100)}%`, background: ACCENT }}
        />
      ))}
    </div>
  );
}

export default function BillingSection({ organizationId, orgName, env, onMetrics }) {
  const [ref, inView] = useInView();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotConfigured(false);
    try {
      const json = await fetchOrgBillingDetail(
        organizationId,
        { monthAndYear: currentMonthAndYear() },
        env,
      );
      const bucket = Array.isArray(json) ? json[0] : json;
      setData(bucket || null);
      const summary = bucket?.summary || {};
      onMetrics?.({
        billableLoans: summary.billableLoans,
        totalCharge: summary.totalCharge,
      });
    } catch (err) {
      const message = err?.message || "Failed to load billing usage";
      if (/billing|configur/i.test(message)) {
        setNotConfigured(true);
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }, [organizationId, env, onMetrics]);

  useEffect(() => {
    if (inView) load();
  }, [inView, load]);

  const summary = data?.summary || {};
  const loans = data?.loans || [];

  return (
    <SectionCard
      ref={ref}
      title="Billing"
      subtitle="Per-loan billing usage this month"
      icon={<FaFileInvoiceDollar />}
      accent={ACCENT}
      loading={(loading || !inView) && !data && !notConfigured}
      error={error}
      onRetry={load}
      size="wide"
      link={{
        to: `/enterprise-billing-usage/${organizationId}`,
        label: "Open Enterprise Billing dashboard",
        state: { organizationName: orgName },
      }}
    >
      {notConfigured ? (
        <div className="oo-empty-hint">
          Billing is not configured for this organization. Set a per-loan rate
          to unlock usage tracking.
        </div>
      ) : (
        <>
          <div className="oo-ministat-row">
            <MiniStat label="Billable" value={fmtN(summary.billableLoans)} accent={ACCENT} />
            <MiniStat label="Total loans" value={fmtN(summary.totalLoans)} />
            <MiniStat label="Cost" value={fmtMoney(summary.totalCharge)} />
          </div>
          <DailyBars loans={loans} />
          <div className="oo-chip">
            Rate:{" "}
            <strong>
              {summary.discountTier?.ratePerLoan != null
                ? fmtMoney(summary.discountTier.ratePerLoan)
                : "—"}
            </strong>
            {summary.discountTier?.discountPercent
              ? ` · ${summary.discountTier.discountPercent}% off`
              : ""}
          </div>
        </>
      )}
    </SectionCard>
  );
}
